import { find } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { telegramFlowApi, p2pProvidersApi } from 'api';
import { DataGridColumnDefinition } from 'components';
import { QueryKey, FilterDefinitionType } from 'enums';
import { usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { TelegramFlow, FilterDefinition, TelegramUser } from 'types';
import { formUtils } from 'utils';

import { TelegramFlowContext } from '../TelegramFlowDialog';
import { TelegramFlowTab } from '../TelegramFlowTab';
import { TelegramFlowFilters } from '../types';

type ProvidersTabProps = {
  telegramUsers: TelegramUser[];
};

export const ProvidersTab: React.FC<ProvidersTabProps> = ({
  telegramUsers,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow',
  });

  const queryResult = usePartialQuery(
    QueryKey.TelegramFlowProviders,
    telegramFlowApi.getP2PProvidersPaginated,
  );

  const queryResultProviders = useQuery(QueryKey.P2PProviders, () =>
    p2pProvidersApi.getAll(),
  );

  const entityColumn: DataGridColumnDefinition<TelegramFlow> = {
    header: t('fields.p2p_provider'),
    valueGetter: (item) => item.p2pProvider?.name || item.p2pProviderId,
  };

  const filtersDefinitions: FilterDefinition<TelegramFlowFilters>[] = useMemo(
    () => [
      {
        label: t('filters.p2p_provider'),
        name: 'p2pProviderId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(queryResultProviders.data || []),
        getDisplayName: (p2pProviderId: string) =>
          find(queryResultProviders.data, { id: p2pProviderId })?.name,
      },
      {
        label: t('filters.chat_id'),
        name: 'chatId',
        type: FilterDefinitionType.Text,
      },
    ],
    [t, queryResultProviders.data],
  );

  return (
    <TelegramFlowTab
      filtersDefinitions={filtersDefinitions}
      entityColumn={entityColumn}
      queryResult={queryResult}
      context={TelegramFlowContext.P2PProvider}
      telegramUsers={telegramUsers}
    />
  );
};
